import React, { createContext, useContext, useState, useEffect } from 'react';
import { QuickNote, NotesContextType } from '../types/notes';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

const NotesContext = createContext<NotesContextType | undefined>(undefined);

export function NotesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState<QuickNote[]>([]);

  // Load notes from localStorage when user logs in
  useEffect(() => {
    if (user) {
      const savedNotes = localStorage.getItem(`quick_notes_${user.uid}`);
      if (savedNotes) {
        setNotes(JSON.parse(savedNotes));
      }
    } else {
      setNotes([]);
    }
  }, [user]);

  // Save notes to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(`quick_notes_${user.uid}`, JSON.stringify(notes));
    }
  }, [notes, user]);

  const addNote = (noteData: Omit<QuickNote, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) {
      toast.error('Please log in to add notes');
      return;
    }

    const newNote: QuickNote = {
      ...noteData,
      id: Date.now().toString() + Math.random().toString(36).substring(2),
      userId: user.uid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setNotes(prev => [newNote, ...prev]);
    toast.success('Note added successfully');
  };

  const updateNote = (id: string, updates: Partial<QuickNote>) => {
    setNotes(prev => prev.map(note => 
      note.id === id 
        ? { ...note, ...updates, updatedAt: new Date().toISOString() }
        : note
    ));
    toast.success('Note updated successfully');
  };

  const deleteNote = (id: string) => {
    setNotes(prev => prev.filter(note => note.id !== id));
    toast.success('Note deleted successfully');
  };

  const pinNote = (id: string) => {
    updateNote(id, { isPinned: true });
  };

  const unpinNote = (id: string) => {
    updateNote(id, { isPinned: false });
  };

  const getNotesByCategory = (category: string) => {
    return notes.filter(note => note.category === category);
  };

  const searchNotes = (query: string) => {
    const lowercaseQuery = query.toLowerCase();
    return notes.filter(note => 
      note.title.toLowerCase().includes(lowercaseQuery) ||
      note.content.toLowerCase().includes(lowercaseQuery) ||
      note.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
    );
  };

  return (
    <NotesContext.Provider value={{
      notes,
      addNote,
      updateNote,
      deleteNote,
      pinNote,
      unpinNote,
      getNotesByCategory,
      searchNotes
    }}>
      {children}
    </NotesContext.Provider>
  );
}

export function useNotes() {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
}